import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { Bag, CheckCircle, CreditCard, DeviceMobile, Minus, Plus, XCircle } from '@phosphor-icons/react';
import {
  getStorefront,
  startShopCheckout,
  type Storefront,
  type StorefrontVariant,
} from '@/api/shop_public';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';

// Public buyer-facing storefront (mobile-app §8.4). Routed OUTSIDE RequireAuth:
// the token in the URL is the credential. /store/:token is a team store;
// /store/sell/:token is a member's personal sale link (attribution is
// server-side from the token, never the client). Design-system rules apply —
// this is a supporter-facing surface, same as the public invoice page.
export function PublicStorefront({ seller = false }: { seller?: boolean }) {
  const { token = '' } = useParams();
  const [searchParams] = useSearchParams();
  const outcome = searchParams.get('checkout');
  const { t } = useTranslation('common');

  const sfQuery = useQuery({
    queryKey: ['storefront', seller, token],
    queryFn: () => getStorefront(token, seller),
    enabled: !!token,
    retry: (n, e) => !(e instanceof ApiError && (e.status === 404 || e.status === 410)) && n < 2,
  });

  // Cart: variant id → quantity. Kept flat so the total + line build are trivial.
  const [cart, setCart] = useState<Record<string, number>>({});
  const setQty = (variantID: string, qty: number) =>
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[variantID];
      else next[variantID] = qty;
      return next;
    });

  if (sfQuery.isPending) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      </Shell>
    );
  }
  if (sfQuery.error instanceof ApiError) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 rounded-3xl border border-border/60 bg-card p-8 text-center">
          <XCircle weight="fill" className="text-amber-600" size={48} />
          <h1 className="text-xl font-bold tracking-tight">
            {t('storefront.error.unavailable', {
              defaultValue: 'This store link is no longer valid.',
            })}
          </h1>
        </div>
      </Shell>
    );
  }

  const sf = sfQuery.data!;
  if (outcome === 'success') {
    return (
      <Shell>
        <StoreHeader sf={sf} />
        <div className="flex flex-col items-center gap-2 rounded-3xl bg-emerald-50 p-6 text-center text-emerald-900">
          <CheckCircle weight="fill" size={40} />
          <p className="text-lg font-semibold">
            {t('storefront.paidTitle', { defaultValue: 'Thanks for your order!' })}
          </p>
          <p className="text-sm">
            {t('storefront.paidBody', { defaultValue: 'A confirmation was sent to your email.' })}
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <StoreHeader sf={sf} />
      {sf.products.length === 0 ? (
        <p className="rounded-3xl border border-border/60 bg-card p-6 text-sm text-muted-foreground">
          {t('storefront.empty', { defaultValue: 'This store has nothing for sale right now.' })}
        </p>
      ) : (
        <>
          <section className="flex flex-col gap-3">
            {sf.products.map((p) => (
              <article key={p.id} className="rounded-3xl border border-border/60 bg-card p-5">
                <h2 className="text-base font-semibold tracking-tight">{p.title}</h2>
                {p.description && (
                  <p className="mt-0.5 text-sm text-muted-foreground">{p.description}</p>
                )}
                <ul className="mt-3 flex flex-col divide-y divide-border/40">
                  {p.variants.map((v) => (
                    <VariantRow
                      key={v.id}
                      variant={v}
                      qty={cart[v.id] ?? 0}
                      onQty={(q) => setQty(v.id, q)}
                    />
                  ))}
                </ul>
              </article>
            ))}
          </section>
          <CartPanel sf={sf} cart={cart} seller={seller} token={token} />
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 md:p-8">{children}</div>;
}

function StoreHeader({ sf }: { sf: Storefront }) {
  const { t } = useTranslation('common');
  return (
    <header className="flex flex-col gap-1 rounded-3xl border border-border/60 bg-card p-6">
      <span className="flex items-center gap-2 text-lg font-bold tracking-tight">
        <Bag weight="bold" size={26} />
        {sf.name}
      </span>
      {sf.seller_name && (
        <p className="text-sm text-muted-foreground">
          {t('storefront.soldBy', { defaultValue: 'Sold by {{name}}', name: sf.seller_name })}
        </p>
      )}
    </header>
  );
}

function VariantRow({
  variant,
  qty,
  onQty,
}: {
  variant: StorefrontVariant;
  qty: number;
  onQty: (q: number) => void;
}) {
  return (
    <li className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0 flex-1 text-sm">
        <p className="truncate font-medium">
          {variant.sku || formatMoney(variant.price_minor, variant.currency)}
        </p>
        {variant.sku && (
          <p className="text-xs text-muted-foreground">
            {formatMoney(variant.price_minor, variant.currency)}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="decrease"
          disabled={qty <= 0}
          onClick={() => onQty(qty - 1)}
        >
          <Minus weight="bold" />
        </Button>
        <span className="w-6 text-center text-sm tabular-nums">{qty}</span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="increase"
          onClick={() => onQty(qty + 1)}
        >
          <Plus weight="bold" />
        </Button>
      </div>
    </li>
  );
}

function CartPanel({
  sf,
  cart,
  seller,
  token,
}: {
  sf: Storefront;
  cart: Record<string, number>;
  seller: boolean;
  token: string;
}) {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [method, setMethod] = useState<'card' | 'swish'>('card');
  const [swishQR, setSwishQR] = useState<string | null>(null);
  const [swishDeepLink, setSwishDeepLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Flatten the store's variants so we can price the cart.
  const variantIndex = useMemo(() => {
    const m = new Map<string, StorefrontVariant>();
    for (const p of sf.products) for (const v of p.variants) m.set(v.id, v);
    return m;
  }, [sf]);

  const lines = Object.entries(cart).filter(([, q]) => q > 0);
  const currency = variantIndex.get(lines[0]?.[0] ?? '')?.currency ?? '';
  const totalMinor = lines.reduce(
    (sum, [id, q]) => sum + (variantIndex.get(id)?.price_minor ?? 0) * q,
    0,
  );

  const checkout = useMutation({
    mutationFn: async () => {
      const base = `${window.location.origin}${window.location.pathname}`;
      return startShopCheckout(token, seller, {
        lines: lines.map(([variant_id, quantity]) => ({ variant_id, quantity })),
        method,
        buyer: { name: name.trim(), email: email.trim() },
        success_url: `${base}?checkout=success`,
        cancel_url: `${base}?checkout=cancel`,
      });
    },
    onSuccess: async (res) => {
      setError(null);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      if (res.swish_qr) {
        setSwishDeepLink(res.swish_deep_link ?? null);
        try {
          setSwishQR(
            await QRCode.toDataURL(res.swish_qr, { errorCorrectionLevel: 'M', margin: 1, scale: 6 }),
          );
        } catch {
          setSwishQR(null);
        }
      }
    },
    onError: (e) => setError(e instanceof ApiError ? e.code : 'unknown'),
  });

  const canPay =
    lines.length > 0 && name.trim() !== '' && email.trim() !== '' && !checkout.isPending;

  if (lines.length === 0) return null;

  return (
    <section className="flex flex-col gap-3 rounded-3xl border border-border/60 bg-card p-6">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t('storefront.total', { defaultValue: 'Total' })}
        </span>
        <span className="text-lg font-bold">{formatMoney(totalMinor, currency)}</span>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        {t('storefront.yourName', { defaultValue: 'Your name' })}
        <input
          className="rounded-2xl border border-border bg-background px-3 py-2"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        {t('storefront.yourEmail', { defaultValue: 'Email for the receipt' })}
        <input
          type="email"
          className="rounded-2xl border border-border bg-background px-3 py-2"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
      </label>

      <div className="flex gap-2">
        <MethodButton active={method === 'card'} onClick={() => setMethod('card')}>
          <CreditCard weight="bold" /> {t('storefront.methodCard', { defaultValue: 'Card' })}
        </MethodButton>
        <MethodButton active={method === 'swish'} onClick={() => setMethod('swish')}>
          <DeviceMobile weight="bold" /> {t('storefront.methodSwish', { defaultValue: 'Swish' })}
        </MethodButton>
      </div>

      <Button type="button" disabled={!canPay} onClick={() => checkout.mutate()} className="self-stretch">
        {checkout.isPending
          ? t('storefront.processing', { defaultValue: 'Processing…' })
          : t('storefront.payCta', {
              defaultValue: 'Pay {{amount}}',
              amount: formatMoney(totalMinor, currency),
            })}
      </Button>

      {error && (
        <p className="text-sm text-red-600">
          {error === 'swish_not_configured'
            ? t('storefront.swishUnavailable', {
                defaultValue: 'Swish isn’t available for this store — pay by card instead.',
              })
            : t('storefront.checkoutError', {
                defaultValue: 'Could not start checkout. Check your details and try again.',
              })}
        </p>
      )}

      {(swishQR || swishDeepLink) && (
        <div className="flex flex-col items-center gap-3 rounded-2xl bg-muted/40 p-4 text-center">
          {swishQR && (
            <img
              src={swishQR}
              alt={t('storefront.swishQrAlt', { defaultValue: 'Swish QR code' })}
              className="h-44 w-44"
            />
          )}
          <p className="text-sm text-muted-foreground">
            {t('storefront.swishScan', {
              defaultValue: 'Scan with the Swish app, or tap below on your phone.',
            })}
          </p>
          {swishDeepLink && (
            <a
              className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] border border-border px-4 py-2 text-sm font-semibold"
              href={swishDeepLink}
            >
              <DeviceMobile weight="bold" size={18} />
              {t('storefront.swishOpenApp', { defaultValue: 'Open the Swish app' })}
            </a>
          )}
        </div>
      )}
    </section>
  );
}

function MethodButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant={active ? 'default' : 'outline'}
      onClick={onClick}
      className="flex-1"
    >
      {children}
    </Button>
  );
}

function formatMoney(minor: number, currency: string): string {
  if (!currency) return (minor / 100).toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}
